import React from "react";
import axios from "axios";

import Loading from "../../components/common/loading";
import NotificationManager from "../../utils/notificationManager";
import ReactPaginate from "react-paginate";

/** 
 * A simple local PaginatedList that caches pages in props.pages = [{ page, devices[] }]
 * and calls `this.props.fetchPage(page, pageSize)` to actually fetch data from the server.
 */
class PaginatedList extends React.Component {
  state = {
    currentPageNumber: 1,
    pageSize: 10, // how many items per page
  };

  componentDidUpdate(prevProps) {
    // If parent changes resetKey, reset to page 1
    if (this.props.resetKey !== prevProps.resetKey) {
      this.setState({ currentPageNumber: 1 });
    }
  }

  findPageInCache = (pageNumber) => {
    const { pages } = this.props;
    if (!Array.isArray(pages)) return null;
    return pages.find((p) => p.page === pageNumber);
  };

  handlePageClick = ({ selected }) => {
    const newPage = selected + 1; // react-paginate is zero-based
    this.setState({ currentPageNumber: newPage }, () => {
      this.fetchPageIfNeeded(newPage);
      // optionally pre-fetch next page:
      this.fetchPageIfNeeded(newPage + 1);
    });
  };

  fetchPageIfNeeded = (pageNumber) => {
    const cachedPage = this.findPageInCache(pageNumber);
    if (cachedPage) return; // We already have data for this page

    const { pageLoading, fetchPage } = this.props;
    if (pageLoading[pageNumber]) {
      // already loading
      console.log(`Already fetching page ${pageNumber}, skipping.`);
      return;
    }
    // Actually fetch from parent
    fetchPage(pageNumber, this.state.pageSize);
  };

  getCurrentPageDevices = () => {
    const currentPageObj = this.findPageInCache(this.state.currentPageNumber);
    return currentPageObj ? currentPageObj.devices : [];
  };

  render() {
    const {
      totalItemCount,
      ItemRenderer,
      pageLoading,
      LoadingProp, // optional skeleton or spinner
      parentProps,
    } = this.props;
    const { currentPageNumber, pageSize } = this.state;

    const totalPages = totalItemCount
      ? Math.ceil(totalItemCount / pageSize)
      : 1;

    // Current page data
    const currentPageDevices = this.getCurrentPageDevices();
    // Are we loading this page?
    const isCurrentPageLoading = !!pageLoading[currentPageNumber];

    // If we have no data for this page & it is loading -> show loader
    const showSpinner = isCurrentPageLoading && currentPageDevices.length === 0;

    return (
      <div>
        {showSpinner && (LoadingProp ? <LoadingProp /> : <Loading black />)}

        {!showSpinner &&
          currentPageDevices.map((device, i) => (
            <ItemRenderer
              key={device.enclosureSerialNumber}
              item={device}
              i={i}
              parentProps={parentProps}
            />
          ))}

        {totalPages > 1 && (
          <div style={{ marginTop: "2rem" }}>
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              breakLabel="..."
              forcePage={currentPageNumber - 1}
              pageCount={totalPages}
              onPageChange={this.handlePageClick}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              containerClassName="react-paginate"
              activeClassName="selected"
            />
          </div>
        )}
      </div>
    );
  }
}

// Example skeleton loader for rows, matching your prior UI styling
const LoadingSection = ({ columnWidths }) => {
  return (
    <div>
      {[0, 1, 2, 3].map((val) => (
        <div className="grid-row-wrapper" key={val}>
          <div className="grid-row">
            <div style={{ flex: columnWidths[0] }} />
            <div style={{ flex: columnWidths[1] }}>
              <div className="skeleton-blank" />
            </div>
            <div style={{ flex: columnWidths[2] }}>
              <div className="skeleton-blank" />
            </div>
            <div style={{ flex: columnWidths[3] }}>
              <div className="skeleton-blank" />
            </div>
            <div style={{ flex: columnWidths[4] }}>
              <div className="skeleton-blank" />
            </div>
            <div style={{ flex: columnWidths[5] }}>
              <div className="skeleton-blank" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Renders a device in the "group devices" list
function GroupDeviceItemRenderer({ item: device, parentProps }) {
  const {
    removeGroupDeviceSerialNumbersLoading,
    removeGroupDevice,
    columnWidths,
  } = parentProps;

  const connectionClass =
    device.connectionState === "Connected" ? "connected" : "disconnected";
  const isRemoving = removeGroupDeviceSerialNumbersLoading.includes(
    device.enclosureSerialNumber
  );

  return (
    <div
      className={`grid-row-wrapper ${connectionClass}`}
      style={{ opacity: 1 }}
      title={device.connectionState === "Connected" ? "" : "Disconnected"}
    >
      <div className={`grid-row ${connectionClass}`} style={{ minWidth: "200px" }}>
        <div style={{ flex: columnWidths[1] }}>
          <span style={{ display: "flex", flexDirection: "column" }}>
            <span>
              {device.deviceName}
            </span>
          </span>
        </div>
        <div style={{ flex: columnWidths[2] }}>{device.modelNumber}</div>
        <div style={{ flex: columnWidths[3] }}>
          {device.enclosureSerialNumber}
        </div>
        <div style={{ flex: columnWidths[4] }}>{device.connectionState}</div>
        <div style={{ flex: columnWidths[5], display: "flex", justifyContent: "flex-end" }}>
          {isRemoving ? (
            <Loading black />
          ) : (
            <span
              onClick={() => removeGroupDevice(device.enclosureSerialNumber)}
              style={{
                cursor: "pointer",
                backgroundColor: "grey",
                color: "white",
                padding: "3px 15px 3px 10px",
                borderRadius: "3px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <i className="material-icons">remove</i>
              Remove
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Renders a device in the "available devices" list
function AvailableDeviceItemRenderer({ item: device, parentProps }) {
  const {
    addGroupDeviceSerialNumbersLoading,
    addGroupDevice,
    alreadyInGroupSet, // a Set of serialNumbers
    columnWidths,
  } = parentProps;

  const connectionClass =
    device.connectionState === "Connected" ? "connected" : "disconnected";
  const isAdding = addGroupDeviceSerialNumbersLoading.includes(
    device.enclosureSerialNumber
  );
  const isInGroup = alreadyInGroupSet.has(device.enclosureSerialNumber);

  return (
    <div
      className={`grid-row-wrapper ${connectionClass}`}
      style={{ opacity: 1 }}
      title={device.connectionState === "Connected" ? "" : "Disconnected"}
    >
      <div className={`grid-row ${connectionClass}`} style={{ minWidth: "200px" }}>
        <div style={{ flex: columnWidths[1] }}>
          <span style={{ display: "flex", flexDirection: "column" }}>
            <span>
              {device.deviceName}
            </span>
          </span>
        </div>
        <div style={{ flex: columnWidths[2] }}>{device.modelNumber}</div>
        <div style={{ flex: columnWidths[3] }}>
          {device.enclosureSerialNumber}
        </div>
        <div style={{ flex: columnWidths[4] }}>{device.connectionState}</div>
        <div style={{ flex: columnWidths[5], display: "flex", justifyContent: "flex-end" }}>
          {isInGroup ? (
            <span
              style={{
                backgroundColor: "#ccc",
                color: "#444",
                padding: "3px 15px 3px 10px",
                borderRadius: "3px",
                display: "flex",
                alignItems: "center",
                opacity: 0.8,
              }}
            >
              <i className="material-icons">done</i>
              Already in group
            </span>
          ) : isAdding ? (
            <Loading black />
          ) : (
            <span
              style={{
                cursor: "pointer",
                backgroundColor: "#223647",
                color: "white",
                padding: "3px 15px 3px 10px",
                borderRadius: "3px",
                display: "flex",
                alignItems: "center",
              }}
              onClick={() => addGroupDevice(device.enclosureSerialNumber)}
            >
              <i className="material-icons">add</i>
              Add
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default class ManageGroupDevices extends React.Component {
  state = {
    // Pagination data for group devices
    groupDevicesPages: [],
    groupDevicesPageLoading: {},
    groupDevicesTotalCount: 0,
    groupDevicesResetKey: 0,
    groupSearch: "",

    // Pagination data for available devices
    availablePages: [],
    availablePageLoading: {},
    availableTotalCount: 0,
    availableResetKey: 0,
    availableSearch: "",

    // Add/Remove loading states
    addGroupDeviceSerialNumbersLoading: [],
    removeGroupDeviceSerialNumbersLoading: [],
  };

  // We rely on this.props.resources.groups to find the group
  getGroup = () => {
    const groupId = this.props.match.params.groupId;
    return (
      this.props.resources.groups.find((g) => g.id == groupId) || {
        name: "Unknown Group",
      }
    );
  };

  componentDidMount() {
    this.ensureGroupsLoaded();
    this.fetchPagesIfReady(true);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.resources.groupsLoaded !== this.props.resources.groupsLoaded &&
      !this.props.resources.groupsLoaded &&
      !this.props.resources.groupsLoading
    ) {
      this.ensureGroupsLoaded();
    }

    if (
      (!prevProps.resources.groupsLoaded && this.props.resources.groupsLoaded) ||
      prevProps.match.params.groupId !== this.props.match.params.groupId
    ) {
      this.fetchPagesIfReady(true);
    }
  }

  ensureGroupsLoaded = () => {
    const { resources = {}, fetchResource } = this.props;
    if (!resources.groupsLoaded && !resources.groupsLoading) {
      fetchResource("groups");
    }
  };

  fetchPagesIfReady = (reset = false) => {
    const group = this.getGroup();
    if (!group.id) {
      return;
    }

    this.fetchGroupDevicesPage(1, reset);
    this.fetchAvailableDevicesPage(1, reset);
  };

  /* -----------------------------------------------------------
   * Fetch group devices (in group) 
   * ----------------------------------------------------------- */
  fetchGroupDevicesPage = (pageNumber, reset = false) => {
    const group = this.getGroup();
    if (!group.id) return; // If group not found

    if (reset) {
      this.setState({
        groupDevicesPages: [],
        groupDevicesPageLoading: {},
        groupDevicesResetKey: this.state.groupDevicesResetKey + 1,
      });
    }

    this.setState((prev) => ({
      groupDevicesPageLoading: {
        ...prev.groupDevicesPageLoading,
        [pageNumber]: true,
      },
    }));

    const limit = 10;
    const { groupSearch } = this.state;

    axios
      .get("/v2/devices", {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        params: {
          page: pageNumber,
          limit,
          search: groupSearch,
          groupId: group.id,
          inGroup: 1,
        },
      })
      .then((res) => {
        const newDevices = res.data.data || [];
        const totalCount = res.data.meta?.totalCount || 0;

        this.setState((prev) => {
          const filteredPages = prev.groupDevicesPages.filter(
            (p) => p.page !== pageNumber
          );
          const newPage = { page: pageNumber, devices: newDevices };
          return {
            groupDevicesPages: [...filteredPages, newPage],
            groupDevicesTotalCount: totalCount,
            groupDevicesPageLoading: {
              ...prev.groupDevicesPageLoading,
              [pageNumber]: false,
            },
          };
        });
      })
      .catch((err) => {
        console.error(err);
        this.setState((prev) => ({
          groupDevicesPageLoading: {
            ...prev.groupDevicesPageLoading,
            [pageNumber]: false,
          },
        }));
      });
  };

  applyGroupSearch = () => {
    this.fetchGroupDevicesPage(1, true);
  };

  removeGroupDevice = (enclosureSerialNumber) => {
    this.setState((prevState) => ({
      removeGroupDeviceSerialNumbersLoading: [
        ...prevState.removeGroupDeviceSerialNumbersLoading,
        enclosureSerialNumber,
      ],
    }));

    const group = this.getGroup();
    axios
      .post(
        `/v1/groups/${group.id}/removeDevice`,
        { enclosureSerialNumber },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      )
      .then((res) => {
        this.setState((prevState) => ({
          removeGroupDeviceSerialNumbersLoading:
            prevState.removeGroupDeviceSerialNumbersLoading.filter(
              (sn) => sn !== enclosureSerialNumber
            ),
        }));
        if (res.status === 200) {
          NotificationManager.success("Device removed from group");
          // refresh in-group devices
          this.fetchGroupDevicesPage(1, true);
          // also refresh available so that device re-appears
          this.fetchAvailableDevicesPage(1, true);
        } else {
          NotificationManager.alert("Failed to remove device from group");
        }
      })
      .catch((err) => {
        console.error(err);
        this.setState((prevState) => ({
          removeGroupDeviceSerialNumbersLoading:
            prevState.removeGroupDeviceSerialNumbersLoading.filter(
              (sn) => sn !== enclosureSerialNumber
            ),
        }));
        NotificationManager.alert("Failed to remove device from group");
      });
  };

  /* -----------------------------------------------------------
   * Fetch available devices (NOT in group)
   * ----------------------------------------------------------- */
  fetchAvailableDevicesPage = (pageNumber, reset = false) => {
    const group = this.getGroup();
    if (!group.id) return;

    if (reset) {
      this.setState({
        availablePages: [],
        availablePageLoading: {},
        availableResetKey: this.state.availableResetKey + 1,
      });
    }

    this.setState((prev) => ({
      availablePageLoading: {
        ...prev.availablePageLoading,
        [pageNumber]: true,
      },
    }));

    const limit = 10;
    const { availableSearch } = this.state;

    axios
      .get("/v2/devices", {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        params: {
          page: pageNumber,
          limit,
          search: availableSearch,
          // groupId: group.id,
          // notInGroup: 1,
        },
      })
      .then((res) => {
        const newDevices = res.data.data || [];
        const totalCount = res.data.meta?.totalCount || 0;

        this.setState((prev) => {
          const filteredPages = prev.availablePages.filter(
            (p) => p.page !== pageNumber
          );
          const newPage = { page: pageNumber, devices: newDevices };
          return {
            availablePages: [...filteredPages, newPage],
            availableTotalCount: totalCount,
            availablePageLoading: {
              ...prev.availablePageLoading,
              [pageNumber]: false,
            },
          };
        });
      })
      .catch((err) => {
        console.error(err);
        this.setState((prev) => ({
          availablePageLoading: {
            ...prev.availablePageLoading,
            [pageNumber]: false,
          },
        }));
      });
  };

  applyAvailableSearch = () => {
    this.fetchAvailableDevicesPage(1, true);
  };

  addGroupDevice = (enclosureSerialNumber) => {
    this.setState((prevState) => ({
      addGroupDeviceSerialNumbersLoading: [
        ...prevState.addGroupDeviceSerialNumbersLoading,
        enclosureSerialNumber,
      ],
    }));

    const group = this.getGroup();
    axios
      .post(
        `/v1/groups/${group.id}/addDevice`,
        { enclosureSerialNumber },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      )
      .then((res) => {
        this.setState((prevState) => ({
          addGroupDeviceSerialNumbersLoading:
            prevState.addGroupDeviceSerialNumbersLoading.filter(
              (sn) => sn !== enclosureSerialNumber
            ),
        }));
        if (res.status === 200) {
          NotificationManager.success("Device added to group");
          // refresh both lists
          this.fetchGroupDevicesPage(1, true);
          this.fetchAvailableDevicesPage(1, true);
        } else {
          NotificationManager.alert("Failed to add device to group");
        }
      })
      .catch((err) => {
        console.error(err);
        this.setState((prevState) => ({
          addGroupDeviceSerialNumbersLoading:
            prevState.addGroupDeviceSerialNumbersLoading.filter(
              (sn) => sn !== enclosureSerialNumber
            ),
        }));
        NotificationManager.alert("Failed to add device to group");
      });
  };

  /* 
   * Gather a Set of all devices in the group so that “available list”
   * can check if something is “already in group.”
   */
  collectAllInGroupSerials = () => {
    // Flatten all groupDevicesPages
    const out = new Set();
    this.state.groupDevicesPages.forEach((p) => {
      p.devices.forEach((d) => {
        out.add(d.enclosureSerialNumber);
      });
    });
    return out;
  };

  render() {
    const foundGroup = this.getGroup();
    const columnWidths = [1, 8, 4, 4, 4, 4];

    const {
      groupDevicesPages,
      groupDevicesPageLoading,
      groupDevicesTotalCount,
      groupDevicesResetKey,
      groupSearch,

      availablePages,
      availablePageLoading,
      availableTotalCount,
      availableResetKey,
      availableSearch,

      addGroupDeviceSerialNumbersLoading,
      removeGroupDeviceSerialNumbersLoading,
    } = this.state;

    return (
      <div>
        {/* ======================== GROUP DEVICES ========================= */}
        {/** Only show if we have some group devices or the user can search */}
        <div
          style={{
            // overflowX: "auto",
            // maxHeight: "45vh",
          }}
        >
          <div className="grid-x " style={{ width: "100%", marginBottom: "10px" }}>
            <div
              className="cell "
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ marginTop: "20px", marginLeft: "5px" }}>
                <h5 style={{ marginBottom: "0px" }}>
                  {foundGroup.name} Devices
                </h5>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "10px",
                }}
              >
                <input
                  type="text"
                  placeholder="Search for model number, device name, or serial number"
                  style={{
                    marginBottom: "0px",
                    minWidth: "420px",
                  }}
                  value={groupSearch}
                  onChange={(e) => {
                    this.setState({ groupSearch: e.target.value });
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      this.applyGroupSearch();
                    }
                  }}
                />
                <span
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    padding: "0.45rem",
                    height: "2.43rem",
                    margin: "0 0 1rem",
                    marginBottom: "0px",
                    borderRadius: "0.1875rem",
                    backgroundColor: "#49535E",
                    color: "#fff",
                  }}
                  onClick={() => {
                    this.applyGroupSearch();
                  }}
                >
                  <i className="material-icons" style={{ marginRight: "0px" }}>
                    search
                  </i>
                </span>
              </div>
            </div>
          </div>

          {/* Table Header */}
          <div
            className="grid-row header"
            style={{ marginLeft: "5px", minWidth: "200px" }}
          >
            <div style={{ flex: columnWidths[1] }}>Device Name</div>
            <div style={{ flex: columnWidths[2] }}>Model Number</div>
            <div style={{ flex: columnWidths[3] }}>Serial Number</div>
            <div style={{ flex: columnWidths[4] }}>Status</div>
            <div style={{ flex: columnWidths[5] }}></div>
          </div>

          {/* PaginatedList for group devices */}
          <PaginatedList
            pages={groupDevicesPages}
            pageLoading={groupDevicesPageLoading}
            totalItemCount={groupDevicesTotalCount}
            resetKey={groupDevicesResetKey}
            fetchPage={(page, pageSize) =>
              this.fetchGroupDevicesPage(page, false)
            }
            LoadingProp={() => <LoadingSection columnWidths={columnWidths} />}
            ItemRenderer={GroupDeviceItemRenderer}
            parentProps={{
              removeGroupDeviceSerialNumbersLoading,
              removeGroupDevice: this.removeGroupDevice,
              columnWidths,
            }}
          />
        </div>

        {/* ======================== AVAILABLE DEVICES ========================= */}
        <div
          style={{
            // overflowX: "auto",
            // maxHeight: "45vh",
            marginTop: "30px",
          }}
        >
          <div className="grid-x " style={{ width: "100%", marginBottom: "10px" }}>
            <div
              className="cell "
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ marginTop: "20px", marginLeft: "5px" }}>
                <h5 style={{ marginBottom: "0px" }}>Add a Device</h5>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "10px",
                }}
              >
                <input
                  type="text"
                  placeholder="Search for model number, device name, or serial number"
                  style={{
                    marginBottom: "0px",
                    minWidth: "420px",
                  }}
                  value={availableSearch}
                  onChange={(e) => {
                    this.setState({ availableSearch: e.target.value });
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      this.applyAvailableSearch();
                    }
                  }}
                />
                <span
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    padding: "0.45rem",
                    height: "2.43rem",
                    margin: "0 0 1rem",
                    marginBottom: "0px",
                    borderRadius: "0.1875rem",
                    backgroundColor: "#49535E",
                    color: "#fff",
                  }}
                  onClick={() => {
                    this.applyAvailableSearch();
                  }}
                >
                  <i
                    className="material-icons"
                    style={{ marginRight: "0px" }}
                  >
                    search
                  </i>
                </span>
              </div>
            </div>
          </div>

          {/* Table Header */}
          <div
            className="grid-row header"
            style={{ marginLeft: "5px", minWidth: "200px" }}
          >
            <div style={{ flex: columnWidths[1] }}>Device Name</div>
            <div style={{ flex: columnWidths[2] }}>Model Number</div>
            <div style={{ flex: columnWidths[3] }}>Serial Number</div>
            <div style={{ flex: columnWidths[4] }}>Status</div>
            <div style={{ flex: columnWidths[5] }}></div>
          </div>

          {/* PaginatedList for available devices */}
          <PaginatedList
            pages={availablePages}
            pageLoading={availablePageLoading}
            totalItemCount={availableTotalCount}
            resetKey={availableResetKey}
            fetchPage={(page, pageSize) =>
              this.fetchAvailableDevicesPage(page, false)
            }
            LoadingProp={() => <LoadingSection columnWidths={columnWidths} />}
            ItemRenderer={AvailableDeviceItemRenderer}
            parentProps={{
              addGroupDeviceSerialNumbersLoading,
              addGroupDevice: this.addGroupDevice,
              alreadyInGroupSet: this.collectAllInGroupSerials(),
              columnWidths,
            }}
          />
        </div>
      </div>
    );
  }
}
